// ManDana — Sun Arc: daylight trajectory with a live sun bound to the curve.
// DAY  : X = daylight (Sunrise→Sunset), Y = altitude (hill above horizon).
//        Curve dips BELOW the horizon on each side to seat Fajr & Isha.
// NIGHT: once Maghrib arrives the arc FLIPS upside-down — X now runs
//        Sunset(Maghrib)→Sunrise(next day), and the curve becomes a valley BELOW
//        the horizon. Isha (just after sunset) and Fajr (just before sunrise) sit as
//        dipped nodes INSIDE it; a cool "moon" travels from sunset, passing them,
//        and at Fajr the arc flips back to the day hill.
const { ink: inkSA, mdIsLight } = window;

function mdLerpHex(a, b, t) {
  const pa = parseInt(a.slice(1), 16),pb = parseInt(b.slice(1), 16);
  const ar = pa >> 16 & 255,ag = pa >> 8 & 255,ab = pa & 255;
  const br = pb >> 16 & 255,bg = pb >> 8 & 255,bb = pb & 255;
  const r = Math.round(ar + (br - ar) * t),g = Math.round(ag + (bg - ag) * t),bl = Math.round(ab + (bb - ab) * t);
  return `rgb(${r},${g},${bl})`;
}

function SunArc({ accent = '#FFC978', nowSec, preview = false }) {
  const W = 344, H = 108;
  // Prayer points are sourced straight from the shared prayer-time data (the same
  // MD_PRAYERS the schedule renders) so the arc's spacing always reflects the real
  // times — every point's X is proportional to its actual clock minute. Sunrise is
  // its own marker: the day band starts from it and the night arc ends at it. If
  // per-day prayer times are ever introduced, this picks them up for free.
  const PT = {}; (window.MD_PRAYERS || []).forEach((p) => { PT[p.id] = p.min; });
  const P = {
    fajr: PT.fajr ?? 282, dhuhr: PT.dhuhr ?? 788, asr: PT.asr ?? 1015,
    maghrib: PT.maghrib ?? 1214, isha: PT.isha ?? 1293,
    sunrise: (window.MD_SUNRISE && window.MD_SUNRISE.min) ?? 361,
  };
  const light = mdIsLight();

  // shared vertical frame: a horizon line, a hill that rises ABOVE it (day) and a
  // valley that falls BELOW it (night). Day keeps its horizon low so the hill has
  // headroom from the countdown; night centres the horizon so the flipped valley
  // has room to swing into a fuller, more graceful curve.
  const yHorDay = 0.62 * H, peakY = 15, amp = yHorDay - peakY;
  const yHorNight = H - yHorDay;                     // a true vertical mirror of the day horizon
  const nAmp = amp;                                  // same amplitude as the day hill — mirror, not a separate shape

  const now = nowSec / 60;
  const fajrNext = P.fajr + 1440;                    // Fajr of the NEXT day (the moon's finish line)
  const sunriseNext = P.sunrise + 1440;              // Sunrise of the NEXT day — the night arc's right horizon crossing
  // NIGHT is active (live only) from Maghrib through midnight until Fajr
  const night = !preview && (now >= P.maghrib || now < P.fajr);
  // normalise a pre-dawn "now" onto the night continuum (…→ 1440+ minutes)
  const nowPos = night && now < P.fajr ? now + 1440 : now;

  // ── DAY geometry (hill) ──
  const sr = P.sunrise, ss = P.maghrib, span = ss - sr;
  const srX = 0.115 * W, ssX = 0.885 * W, D = ssX - srX;
  const xForDay = (t) => srX + (t - sr) * (D / span);
  const altDay = (t) => Math.sin(Math.PI * (t - sr) / span);   // 0 at sunrise/sunset, 1 at noon, <0 outside
  const yForDay = (t) => yHorDay - altDay(t) * amp;

  // ── NIGHT geometry (valley) : X = Maghrib(sunset) → Sunrise(next day) ──
  // The night runs from sunset to SUNRISE (not to Fajr): Fajr is dawn, a little
  // BEFORE sunrise, so — exactly as the day hill seats Fajr/Isha below the horizon —
  // the valley seats Isha (just after sunset) and Fajr (just before sunrise) as dipped
  // nodes, with the two horizon crossings being the true sunset & sunrise.
  const nm = P.maghrib, nSpan = sunriseNext - nm;
  // mirror the day exactly: the night band crosses the horizon at the SAME inset x
  // as sunrise/sunset, and its twilight tails run out to the same card-edge tips, so
  // the moon arc sweeps into the corners just as wide as the sun arc
  const nmX = srX, nfX = ssX, Dn = nfX - nmX;
  const nT = (t) => t < P.fajr ? t + 1440 : t;       // lift pre-dawn times onto the night axis
  const xForNight = (t) => nmX + (nT(t) - nm) * (Dn / nSpan);
  const altNight = (t) => -Math.sin(Math.PI * (nT(t) - nm) / nSpan);   // negative → dips below horizon
  const yForNight = (t) => yHorNight - altNight(t) * nAmp;

  // pick the active geometry
  const xFor = night ? xForNight : xForDay;
  const yFor = night ? yForNight : yForDay;
  const yHor = night ? yHorNight : yHorDay;          // active horizon (line + scrubber)

  // endpoints & curve extents for the active mode
  const arcStart = night ? P.maghrib : sr;
  const arcEnd = night ? sunriseNext : ss;
  const startX = night ? nmX : srX;
  const endX = night ? nfX : ssX;
  const nTail = 64;                                  // twilight-tail length (min) — sweeps the valley out to the card edges
  const t0 = night ? P.maghrib - nTail : P.fajr - 14;
  const t1 = night ? sunriseNext + nTail : P.isha + 14;

  const samp = (a, b, n) => { const o = []; for (let i = 0; i <= n; i++) { const t = a + (b - a) * i / n; o.push([xFor(t), yFor(t)]); } return o; };
  const toPath = (pts) => 'M' + pts.map((p) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' L');

  // full faint trajectory for the active mode
  const fullCurve = toPath(samp(t0, t1, 120));

  // band fill (under the hill / inside the valley)
  const bandPts = samp(arcStart, arcEnd, 90);
  const bandFill = `M${startX.toFixed(1)},${yHor.toFixed(1)} L` +
    bandPts.map((p) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' L') +
    ` L${endX.toFixed(1)},${yHor.toFixed(1)} Z`;

  // live orb position (clamped to the visible curve extents)
  const cn = Math.max(t0, Math.min(t1, nowPos));
  const sunX = xFor(cn), sunY = yFor(cn);
  // warmth: cool near horizon → warm at the day peak; night holds the full accent
  // so the moon arc keeps the same purple hue (and hue-shadow) as the daytime sun
  // the cool dip is intentionally gentle (floored) so the orb only HINTS cooler near
  // the horizon as a micro-interaction, rather than reading as a blue colour change
  const warmthRaw = night ? 1 : Math.max(0, Math.min(1, altDay(now)));
  const warmth = night ? 1 : 0.6 + 0.4 * warmthRaw;
  const accHex = (typeof accent === 'string' && accent[0] === '#' && accent.length === 7) ? accent : '#FFC978';
  const dawn = '#A6AEEA';                                        // soft cool dusk tone, kept close to the accent
  const sunC = mdLerpHex(dawn, accHex, warmth);
  // lower-edge shade — keep it in the accent's own hue (a deep violet, not navy) so
  // the orb's hue-shadow never reads blue; kept gentle in intensity
  const sunDeep = mdLerpHex(sunC, '#191229', 0.16);
  // the travelled-arc gradient starts cool at dawn by day, but a soft purple by night
  const arcStartTone = night ? mdLerpHex(accHex, '#ffffff', 0.4) : dawn;

  // travelled vs. remaining along the active arc
  // The tail starts from the first prayer node: Fajr (day) or Maghrib (night)
  const elapsedStartT = night ? P.maghrib : P.fajr;
  const elapsedStartX = night ? nmX : xForDay(P.fajr);
  const cnArc = Math.max(arcStart, Math.min(arcEnd, nowPos));
  const elapsedCurve = toPath(samp(elapsedStartT, cnArc, 70));
  const remainCurve = toPath(samp(cnArc, arcEnd, 70));

  // nodes
  const dayNodes = [P.dhuhr, P.asr, P.maghrib];        // day: on/at the hill
  const dayQuiet = [P.fajr, P.isha];                   // day: quiet below-horizon dots
  const nightEndpoints = [P.maghrib];                  // night: only the sunset (Maghrib) horizon crossing — sunrise is not a prayer time
  const nightPass = [P.isha, fajrNext];                // night: dipped nodes the moon passes — Isha (after sunset) & Fajr (before sunrise)

  const uid = 'msa';
  return (
    <svg aria-hidden="true" focusable="false" viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: 'block', overflow: 'visible' }} preserveAspectRatio="xMidYMid meet">
      <defs>
        {/* daylight area fill */}
        <linearGradient id={`${uid}-fill`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={accHex} stopOpacity={light ? 0.26 : 0.40} />
          <stop offset="48%" stopColor={accHex} stopOpacity={light ? 0.08 : 0.13} />
          <stop offset="100%" stopColor={accHex} stopOpacity="0" />
        </linearGradient>
        {/* night valley fill — accent-tinted, matching the daytime hill */}
        <linearGradient id={`${uid}-nfill`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={accHex} stopOpacity={light ? 0.22 : 0.34} />
          <stop offset="60%" stopColor={accHex} stopOpacity={light ? 0.07 : 0.13} />
          <stop offset="100%" stopColor={accHex} stopOpacity="0" />
        </linearGradient>
        {/* travelled arc: soft start tone → active tone */}
        <linearGradient id={`${uid}-elapsed`} gradientUnits="userSpaceOnUse"
          x1={elapsedStartX} y1="0" x2={sunX} y2="0">
          <stop offset="0%" stopColor={arcStartTone} />
          <stop offset="100%" stopColor={sunC} />
        </linearGradient>
        {/* purple mist trailing the orb — dissolves to nothing at the tail's start,
           gathers into a dense haze right behind the orb */}
        <linearGradient id={`${uid}-mist`} gradientUnits="userSpaceOnUse"
          x1={elapsedStartX} y1="0" x2={sunX} y2="0">
          <stop offset="0%" stopColor={sunC} stopOpacity="0" />
          <stop offset="55%" stopColor={sunC} stopOpacity={light ? 0.14 : 0.20} />
          <stop offset="100%" stopColor={sunC} stopOpacity={light ? 0.5 : 0.7} />
        </linearGradient>
        {/* sun/moon bloom (soft, no hard shadow) */}
        <radialGradient id={`${uid}-bloom`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={sunC} stopOpacity={light ? 0.5 : 0.62} />
          <stop offset="42%" stopColor={sunC} stopOpacity="0.16" />
          <stop offset="100%" stopColor={sunC} stopOpacity="0" />
        </radialGradient>
        {/* glossy orb core with offset highlight */}
        <radialGradient id={`${uid}-core`} cx="36%" cy="32%" r="72%">
          <stop offset="0%" stopColor="#FFFFFF" />
          <stop offset="42%" stopColor={sunC} />
          <stop offset="100%" stopColor={sunDeep} />
        </radialGradient>
        {/* luminous underglow for the travelled arc */}
        <filter id={`${uid}-glow`} x="-40%" y="-80%" width="180%" height="320%">
          <feGaussianBlur stdDeviation="3.2" />
        </filter>
        {/* broad, soft blur for the purple mist that fuzzes the orb's tail */}
        <filter id={`${uid}-mistblur`} x="-70%" y="-220%" width="240%" height="540%">
          <feGaussianBlur stdDeviation="7" />
        </filter>
      </defs>

      {/* band fill (hill area in day, valley pocket in night) */}
      <path d={bandFill} fill={night ? `url(#${uid}-nfill)` : `url(#${uid}-fill)`} data-comment-anchor="1858a834d8-path-74-7" />

      {/* horizon baseline */}
      <line x1="6" y1={yHor} x2={W - 6} y2={yHor} stroke={inkSA(0.18)} strokeWidth="1" strokeDasharray="1 5" strokeLinecap="round" />

      {/* full faint trajectory for the active mode */}
      <path d={fullCurve} fill="none" stroke={inkSA(0.15)} strokeWidth="1.5" strokeLinecap="round" />

      {/* still-ahead arc — soft neutral (preview: the whole day band) */}
      <path d={preview ? toPath(bandPts) : remainCurve} fill="none" stroke={inkSA(0.30)} strokeWidth="2" strokeLinecap="round" />

      {/* already travelled — broad purple mist, then a tighter glow (live only) */}
      {!preview && <path d={elapsedCurve} fill="none" stroke={`url(#${uid}-mist)`} strokeWidth="15"
        strokeLinecap="round" filter={`url(#${uid}-mistblur)`} />}
      {!preview && <path d={elapsedCurve} fill="none" stroke={`url(#${uid}-mist)`} strokeWidth="8"
        strokeLinecap="round" filter={`url(#${uid}-glow)`} />}
      {!preview && <path d={elapsedCurve} fill="none" stroke={sunC} strokeOpacity={light ? 0.42 : 0.55} strokeWidth="5"
        strokeLinecap="round" filter={`url(#${uid}-glow)`} />}
      {!preview && <path d={elapsedCurve} fill="none" stroke={`url(#${uid}-elapsed)`} strokeWidth="2.6" strokeLinecap="round" />}

      {/* ── DAY: below-horizon nodes — Fajr gets full activation ring, Isha stays quiet ── */}
      {!night && (() => {
        const fajrPassed = !preview && P.fajr <= now;
        return (<>
          <g>
            <circle cx={xForDay(P.fajr)} cy={yForDay(P.fajr)} r="5" fill={light ? '#fff' : inkSA(0.16)}
              stroke={inkSA(0.14)} strokeWidth="0.75" />
            <circle cx={xForDay(P.fajr)} cy={yForDay(P.fajr)} r="2.7" fill={fajrPassed ? sunC : inkSA(0.42)} />
          </g>
          <circle cx={xForDay(P.isha)} cy={yForDay(P.isha)} r="2.4" fill={inkSA(0.30)} />
        </>);
      })()}

      {/* ── DAY: hill nodes — filled once passed, neutral when upcoming ── */}
      {!night && dayNodes.map((t, i) => {
        const passed = !preview && t <= now;
        return (
          <g key={'d' + i}>
            <circle cx={xForDay(t)} cy={yForDay(t)} r="5" fill={light ? '#fff' : inkSA(0.16)}
              stroke={inkSA(0.14)} strokeWidth="0.75" />
            <circle cx={xForDay(t)} cy={yForDay(t)} r="2.7" fill={passed ? sunC : inkSA(0.42)} />
          </g>
        );
      })}

      {/* ── NIGHT: Maghrib endpoint — full activation ring (always passed once night begins) ── */}
      {night && nightEndpoints.map((t, i) => {
        const passed = nowPos >= t;
        return (
          <g key={'ne' + i}>
            <circle cx={xForNight(t)} cy={yHor} r="5" fill={light ? '#fff' : inkSA(0.16)}
              stroke={inkSA(0.14)} strokeWidth="0.75" />
            <circle cx={xForNight(t)} cy={yHor} r="2.7" fill={passed ? sunC : inkSA(0.42)} />
          </g>
        );
      })}

      {/* ── NIGHT: the dipped Isha & Fajr nodes the moon passes ── */}
      {night && nightPass.map((t, i) => {
        const passed = nowPos >= t;
        return (
          <g key={'np' + i}>
            <circle cx={xForNight(t)} cy={yForNight(t)} r="5" fill={light ? '#fff' : inkSA(0.16)}
              stroke={inkSA(0.14)} strokeWidth="0.75" />
            <circle cx={xForNight(t)} cy={yForNight(t)} r="2.7" fill={passed ? sunC : inkSA(0.42)} />
          </g>
        );
      })}

      {/* scrubber: faint guide from the orb to the horizon (live only) */}
      {!preview && <line x1={sunX} y1={night ? yHor : Math.min(sunY, yHor) + 7} x2={sunX} y2={night ? Math.max(sunY, yHor) - 7 : yHor}
        stroke={sunC} strokeOpacity="0.32" strokeWidth="1.3" strokeDasharray="1 3" />}
      {!preview && <circle cx={sunX} cy={yHor} r="1.8" fill={sunC} fillOpacity="0.55" />}

      {/* the sun / moon — bloom, glossy orb, crisp rim (live only) */}
      {!preview && <circle cx={sunX} cy={sunY} r="23" fill={`url(#${uid}-bloom)`} />}
      {!preview && <circle cx={sunX} cy={sunY} r="7" fill={`url(#${uid}-core)`} />}
      {!preview && <circle cx={sunX} cy={sunY} r="7" fill="none" stroke={sunC} strokeWidth="1.1" strokeOpacity={light ? 0.85 : 0.95} />}
      {!preview && <circle cx={sunX - 2.1} cy={sunY - 2.4} r="1.7" fill="#fff" fillOpacity={light ? 0.75 : 0.92} />}
    </svg>);

}

Object.assign(window, { SunArc });
